import { isNil } from 'lodash';
import React, { useContext } from 'react';
import ReactModal from 'react-modal';
import ArrowPointLeftIcon from '@shared/assets/icons/v2/arrow-point-left.svg';
import ExitIcon from '@shared/assets/icons/v2/exit.svg';
import { MBView } from '@shared/components/MBView';
import { MBSeparator } from '@shared/components/list/separator';
import { MBScrollView } from '@shared/components/scrollview';
import { ColorThemeContext } from '@shared/styles';
// TODO(fant): replace this with react-native-web's Modal
// when we've upgraded React Native Web
import { MBModalButton } from './button';
import { MBModalProps } from './props';
import { Styles, UnvalidatedStyles } from './styles';
import { MBModalTitle } from './title';

// For reference:
// http://reactcommunity.org/react-modal/accessibility/#app-element
// https://github.com/reactjs/react-modal/issues/576
// Note(fant): when running unit tests, we don't want to run this
// since there is no #root element in that case.
if (import.meta.env.MODE !== 'test') {
  ReactModal.setAppElement('#root');
}

/*
    Modal for displaying content over everything else. On web, this is a
    popup in the middle of the screen which darkens the content behind.
    On native it's a full screen takeover.

    Note: closeTimeoutMS lets us apply a transition to animate out the modal:
    https://github.com/reactjs/react-modal/blob/master/docs/styles/transitions.md
*/
export function MBModal({
  title,
  onClose,
  shouldCloseOnOverlayClick = true,
  onBack,
  isCloseButtonHidden,
  visible,
  webOverflow,
  webContainerStyle,
  backgroundColor: backgroundColorOverride,
  children,
  rightButton,
  leftButton,
  animationType,
  webFooter,
  padding = 2.5,
  webOverlayStyle = {},
}: MBModalProps) {
  const colors = useContext(ColorThemeContext);
  const backgroundColor = backgroundColorOverride || colors.background;
  return (
    <ReactModal
      style={{
        content: UnvalidatedStyles.webReactModalContent,
        overlay: { ...UnvalidatedStyles.webReactModalOverlay, ...webOverlayStyle },
      }}
      isOpen={visible}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      className={animationType}
    >
      <MBView style={[Styles.container, webContainerStyle, { backgroundColor }]}>
        {(!!title ||
          !!rightButton ||
          (!!onClose && !isCloseButtonHidden) ||
          !!leftButton ||
          !!onBack) && (
          <MBView style={Styles.titleContainer}>
            {/* 1. render title */}
            {!isNil(title) && <MBModalTitle title={title} shadow={!!backgroundColorOverride} />}

            {/* 2. render right button */}
            {!!rightButton && (
              <MBView center style={Styles.rightButton}>
                {rightButton}
              </MBView>
            )}

            {/* 3. render close button if we have no right button and onClose is enabled */}
            {!rightButton && !!onClose && !isCloseButtonHidden && (
              <MBModalButton
                color={colors.foregroundActive}
                style={Styles.rightButton}
                icon={ExitIcon}
                onPress={onClose}
              />
            )}

            {/* 4. render left button */}
            {!!leftButton && (
              <MBView center style={Styles.leftButton}>
                {leftButton}
              </MBView>
            )}

            {/* 5. render back button if we have no custom left button and onBack is enabled */}
            {!leftButton && !!onBack && (
              <MBModalButton
                color={colors.foregroundActive}
                style={Styles.leftButton}
                icon={ArrowPointLeftIcon}
                onPress={onBack}
                testID="modal-back-button"
              />
            )}
          </MBView>
        )}

        {!!webOverflow && (
          <MBView fill padding={padding}>
            {children}
          </MBView>
        )}
        {!webOverflow && (
          <MBScrollView>
            <MBView padding={padding}>{children}</MBView>
          </MBScrollView>
        )}

        {!!webFooter && (
          <>
            <MBSeparator />
            {webFooter}
          </>
        )}
      </MBView>
    </ReactModal>
  );
}
